import React, { RefObject, useContext } from 'react';
import { ThemeProvider } from 'emotion-theming';
import useSWR from 'swr';
import qs from 'qs';
import { ListResponse, Supplier } from '@ordermentum/ui-types';
import { Theme, ThemeContext } from '../../utilities/theme';
import { ColorCodes, colorsForTheme } from '../../foundations/color';
import { fetcher } from '../../swr/fetchers';
import {
  ContentWrapper,
  OptionContainer,
  OptionHeader,
} from './Switcher.styles';
import SwitcherOption from './SwitcherOption';

type StateProps = {
  params?: { retailerId?: string; supplierId?: string };
  suppliers?: string[];
  external?: boolean;
  showHeader?: boolean;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  const colorThemes = {
    textColor: colors.SPACE,
    iconColor: colors.STORM,
    iconBorderColor: colors.ASH,
    activeColor: colors.PEACH,
    optionRowHeaderColor: colors.STEEL,
  };
  return colorThemes;
};

type themeStyles = {
  textColor: ColorCodes;
  iconColor: ColorCodes;
  iconBorderColor: ColorCodes;
  activeColor: ColorCodes;
  optionRowHeaderColor: ColorCodes;
};

type suppliers = {
  id: string;
  name: string;
  logo?: string;
};

export const SupplierSwitcher = React.forwardRef(
  (props: StateProps, ref: RefObject<HTMLDivElement>) => {
    const themeStyle = getTheme(useContext(ThemeContext));
    const { params, suppliers = [], external, showHeader } = props;
    const supplierId = params?.supplierId;
    let optionList: suppliers[] = [];

    const { data } = useSWR<ListResponse<Supplier>>(
      `/v1/suppliers?${qs.stringify({ id: suppliers })}`,
      fetcher()
    );

    if (!data?.data) {
      return <></>;
    }

    optionList = data.data
      .filter(supplier => suppliers.indexOf(supplier.id) > -1)
      .map(supplier => ({
        id: supplier.id,
        name: supplier.tradingName,
        logo: supplier?.logo?.logo,
      }));

    const getActive = optionId => {
      if (external) return false;
      return supplierId === optionId;
    };

    return (
      <ThemeProvider theme={themeStyle}>
        <ContentWrapper data-testid="supplier-switcher" ref={ref}>
          <OptionContainer>
            {showHeader && <OptionHeader>Suppliers</OptionHeader>}
            {optionList.map(option => (
              <SwitcherOption
                key={option.id}
                option={option}
                url={`/supplier/${option.id}`}
                external={external}
                active={getActive(option.id)}
              />
            ))}
          </OptionContainer>
        </ContentWrapper>
      </ThemeProvider>
    );
  }
);
export default SupplierSwitcher;
